import React, { useEffect, useState } from "react";
import { api, fmtPLN, fmtDateOnly } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { ClipboardCheck, PlusCircle, Receipt, Ticket, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";

export default function EmployeeDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  async function load() {
    try {
      const { data } = await api.get("/dashboard/employee");
      setData(data);
    } catch (e) { toast.error("Błąd wczytywania"); }
  }
  useEffect(() => { load(); }, []);

  // Fallback: API /dashboard/employee zwraca department tylko dla ról POS (BAR/SALA/MASAZ).
  // Dla WAREHOUSE/COOK bierzemy z user context (przypisany w tabeli users). UI audit 2026-07 P1-1.
  const dept = data?.department || user?.department;

  return (
    <div className="space-y-6" data-testid="employee-dashboard">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tt-display">Cześć, {user?.full_name?.split(" ")[0]}!</h1>
        <p className="text-[var(--tt-muted)] mt-1">Twój dział: <span className="font-semibold">{dept || "—"}</span> • dziś: {data?.today}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="employee-tile-grid">
        <Link to="/daily?new=1" className="block" data-testid="employee-module-tile">
          <Card className="tt-card hover:border-[var(--tt-primary)] transition-colors h-full">
            <CardContent className="p-5 flex flex-col items-start gap-3">
              <div className="h-12 w-12 rounded-xl bg-[var(--tt-primary)]/10 text-[var(--tt-primary)] flex items-center justify-center">
                <ClipboardCheck className="h-6 w-6" />
              </div>
              <div className="font-semibold tt-display">Rozliczenie dnia</div>
              <div className="text-sm text-[var(--tt-muted)]">Wprowadź gotówkę, karty, vouchery i wydatki.</div>
            </CardContent>
          </Card>
        </Link>
        {/* P2.1 (2026-07-05): USUNIĘTO kafelek „Dodaj wydatek" dla pracownika.
            Prowadził do formularza wydatku z sejfu — dostęp zablokowany na backendzie (P2.0).
            Wydatki z koperty pracownik dodaje w rozliczeniu dnia (Przepływ A). */}
        <Link to="/shift-checklists" className="block" data-testid="employee-module-tile-checklist">
          <Card className="tt-card hover:border-[var(--tt-primary)] transition-colors h-full">
            <CardContent className="p-5 flex flex-col items-start gap-3">
              <div className="h-12 w-12 rounded-xl bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 flex items-center justify-center">
                <ListChecks className="h-6 w-6" />
              </div>
              <div className="font-semibold tt-display">Wypełnij checklistę</div>
              <div className="text-sm text-[var(--tt-muted)]">Checklista zmiany — start i koniec dnia.</div>
            </CardContent>
          </Card>
        </Link>
        {/* P1 (2026-07): Voucher tile ukryty dla waitress (kelnerka) — martwy link,
            /vouchers route pozwala tylko owner/manager/bartender/receptionist (patrz Sidebar). */}
        {["owner", "manager", "bartender", "receptionist"].includes(user?.role) && (
          <Link to="/vouchers?new=1" className="block" data-testid="employee-module-tile-voucher">
            <Card className="tt-card hover:border-[var(--tt-primary)] transition-colors h-full">
              <CardContent className="p-5 flex flex-col items-start gap-3">
                <div className="h-12 w-12 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center">
                  <Ticket className="h-6 w-6" />
                </div>
                <div className="font-semibold tt-display">Voucher</div>
                <div className="text-sm text-[var(--tt-muted)]">Sprzedaj lub zrealizuj.</div>
              </CardContent>
            </Card>
          </Link>
        )}
      </div>

      <div>
        <h2 className="tt-section-title mb-3">Moje ostatnie rozliczenia</h2>
        {data?.my_reconciliations?.length === 0 ? (
          <Card className="tt-card"><CardContent className="p-6 text-center text-[var(--tt-muted)]">Nie masz jeszcze rozliczeń. Kliknij &ldquo;Rozliczenie dnia&rdquo; aby utworzyć.</CardContent></Card>
        ) : (
          <Card className="tt-card">
            <CardContent className="p-0 divide-y divide-[var(--tt-border)]">
              {data?.my_reconciliations?.map((r) => (
                <div key={r.id} className="p-4 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <StatusBadge value={r.status} />
                      <span className="text-sm font-medium tt-mono">{fmtDateOnly(r.business_date)}</span>
                      <span className="text-xs text-[var(--tt-muted)] uppercase tracking-wide">{r.department}</span>
                    </div>
                    <div className="text-xs text-[var(--tt-muted)] mt-1">Oczek.: {fmtPLN(r.v2_expected_cash ?? r.expected_cash)} • Liczone: {fmtPLN(r.counted_cash)} • Różnica: {fmtPLN(r.v2_cash_variance ?? r.variance)}</div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => navigate(`/daily/${r.id}`)}>Otwórz</Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
